import React, { useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { filterBarStyle } from '../components/filterbar/FilterBar';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import { scrollTo } from '../pages/DigestPage';

export const jumpDown = (
  index: any,
  paperElements: HTMLDivElement[],
  margin: number = 0,
  scrolling: any,
  referenceIndex: any
): void => {
    // do not scroll down at bottom of page
    if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight) {
        return;
    }
    // if already scrolling continuously move down
    if (!scrolling.current) {
        scrolling.current = true;
        referenceIndex.current = index.current;
    }
    referenceIndex.current += 1;
    const id = paperElements[referenceIndex.current].id;
    scrollTo(id, -margin, 'smooth');
};

export const jumpUp = (
  index: any,
  paperElements: HTMLDivElement[],
  margin: number = 0,
  scrolling: any,
  referenceIndex: any
): void => {
    // do not scroll up at top of page
    if (index.current === 0 && window.scrollY + margin <= paperElements[index.current].offsetTop) {
        return;
    }
    let decrement = 1;
    // if already scrolling continuously move up
    if (!scrolling.current) {
        scrolling.current = true;
        referenceIndex.current = index.current;
        // scroll to current paper if focus is in the middle of it
        if (Math.abs((window.scrollY + margin) - paperElements[index.current].offsetTop) > 1) {
            decrement = 0;
        }
    }
    const prevIndex = index.current - decrement;
    const id = paperElements[prevIndex].id;
    index.current = prevIndex;
    scrollTo(id, -margin, 'smooth');
};

interface JumpAndCollapsePanelProps {
    index: React.RefObject<number>;
    paperElements: HTMLDivElement[];
    allCollapsed: boolean;
    setAllCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
    scrolling:  React.RefObject<boolean>;
    referenceIndex: React.RefObject<number>;
    margin: number;
}

const JumpAndCollapsePanel: React.FC<JumpAndCollapsePanelProps> = ({
    index,
    paperElements,
    allCollapsed,
    setAllCollapsed,
    scrolling,
    referenceIndex,
    margin = 0
}) => {
    const [tooltipOpen, setTooltipOpen] = useState({
        jumpDown: false,
        jumpUp: false,
        collapseExpand: false,
        jumpToFirst: false,
    });

    const handleTooltipClose = (key: string) => {
        setTooltipOpen(prev => ({ ...prev, [key]: false }));
    };

    const handleTooltipOpen = (key: string) => {
        setTooltipOpen(prev => ({ ...prev, [key]: true }));
    };

    return (
        <Box sx={{
            ...filterBarStyle,
            ml: 0,
            mt: 0,
            mb: 0,
            marginLeft: '0px',
            marginRight: '0px',
            marginTop: '0px',
            height: '100%',
            padding: {xs: '0px', sm: '10px'},
        }}>
            <Box sx={{ display: { xs: 'none', sm: 'none', md: 'none', lg: 'flex' }}}>
                <Tooltip
                    arrow
                    title='Jump one paper down'
                    placement="top"
                    open={tooltipOpen.jumpDown}
                    onClose={() => handleTooltipClose('jumpDown')}
                    onOpen={() => handleTooltipOpen('jumpDown')}
                >
                    <IconButton onClick={() => {
                        handleTooltipClose('jumpDown');
                        jumpDown(index, paperElements, margin, scrolling, referenceIndex);
                    }}>
                        <ArrowDownwardIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip
                    arrow
                    title='Jump one paper up'
                    placement="top"
                    open={tooltipOpen.jumpUp}
                    onClose={() => handleTooltipClose('jumpUp')}
                    onOpen={() => handleTooltipOpen('jumpUp')}
                >
                    <IconButton onClick={() => {
                        handleTooltipClose('jumpUp');
                        jumpUp(index, paperElements, margin, scrolling, referenceIndex);
                    }}>
                        <ArrowUpwardIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip
                    arrow
                    title='Collapse/Expand all papers'
                    placement="top"
                    open={tooltipOpen.collapseExpand}
                    onClose={() => handleTooltipClose('collapseExpand')}
                    onOpen={() => handleTooltipOpen('collapseExpand')}
                >
                    <IconButton onClick={() => {
                        handleTooltipClose('collapseExpand');
                        setAllCollapsed(prev => !prev);
                    }}>
                        {allCollapsed ? <UnfoldMoreIcon /> : <UnfoldLessIcon />}
                    </IconButton>
                </Tooltip>
            </Box>
            <Box 
                sx={{
                    display: { xs: 'flex'},
                    position: {xs: 'fixed', sm: 'static'},
                    bottom: {xs: '16px', sm: 'auto'},
                    right: { xs: '16px', sm: 'auto' },
                    zIndex: { xs: 1000, sm: 'auto' },
                    padding: '0px',
                }}>
                <Tooltip
                    arrow
                    title='Jump to the first paper'
                    placement="top"
                    open={tooltipOpen.jumpToFirst}
                    onClose={() => handleTooltipClose('jumpToFirst')}
                    onOpen={() => handleTooltipOpen('jumpToFirst')}
                >
                    <IconButton 
                        onClick={() => {
                            handleTooltipClose('jumpToFirst');
                            scrollTo(paperElements[0].id, -margin, 'smooth');
                        }}
                        sx={{
                            width: {xs: '50px', sm: 'auto'},
                            height: {xs: '50px', sm: 'auto'},
                            backgroundColor: { xs: 'white', sm: 'transparent' },
                            borderRadius: '8px',
                            boxShadow: { xs: '0 0 10px #ccc', sm: 'none' },
                            '&:hover': {
                                backgroundColor: { xs: 'white', sm: 'transparent' },
                                boxShadow: { xs: '0 0 10px #ccc', sm: 'none' },
                            },
                        }}
                    >
                        <VerticalAlignTopIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
};

export default JumpAndCollapsePanel;